import { useMemo } from "react";
import { useSelector } from "react-redux";

import BadgeSelect from "@/components/ui/BadgeSelect";
import { Divider } from "@/components/ui/Divider";
import Dropdown from "@/components/ui/dropdown";
import type { IOptionItem } from "@/components/ui/dropdown/types";
import { COUNTRY_DATA } from "@/constants/dataset";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import type { RootState } from "@/store";
import type {
  CountryCode,
  Source,
  SpatialAggregationType
} from "@/types/common";
import type { License } from "@/types/dataset";

import type { DatasetExplorerSessionStorageData } from ".";
import { DataSourceList, type DataSourceProvider } from "./DataSourceList";

type Props = {
  isUseNewDatasetExplorerModal: boolean;
  countryOptions: { label: string; value: CountryCode }[];
  selectedCountry: { label: string; value: CountryCode | "" };
  setSelectedCountry: (value: {
    label: string;
    value: CountryCode | "";
  }) => void;
  dataSource: Source;
  spatialAgg: SpatialAggregationType | "all";
  license: License;
  selectedDataSources: string[];
  dataSourceProviders?: DataSourceProvider[];
  showCatchmentPopup?: boolean;
  setDataSource: (value: Source) => void;
  setSpatialAgg: (value: SpatialAggregationType | "all") => void;
  setCurrentPage: (value: number) => void;
  setLicense: (value: License) => void;
  setSelectedDataSources: (value: string[]) => void;
  sessionStorageData: DatasetExplorerSessionStorageData;
  setSessionItem: (data: DatasetExplorerSessionStorageData) => void;
};

const getSpatialAggregationOptions = (
  showCatchmentPopup = false,
  isLinestringEnabled = false,
  lockedCatchmentSpatialAgg?: SpatialAggregationType,
  isBVTDataForCatchmentEnabled = false
): IOptionItem[] => {
  // Legacy behavior (flag OFF): catchment popup forces "Point" only, all
  // other spatial agg types disabled. BVT flow (flag ON): Point + Polygon
  // allowed, optionally locked to the type of the first added dataset.
  const isLockedTo = (value: SpatialAggregationType) =>
    !!lockedCatchmentSpatialAgg && lockedCatchmentSpatialAgg !== value;
  const polygonDisabledInCatchment = isBVTDataForCatchmentEnabled
    ? isLockedTo("polygon")
    : showCatchmentPopup;
  const pointDisabledInCatchment = isBVTDataForCatchmentEnabled
    ? isLockedTo("poi")
    : false;
  return [
    { label: "All", value: "all", disabled: showCatchmentPopup },
    { label: "Point", value: "poi", disabled: pointDisabledInCatchment },
    {
      label: "Polygon",
      value: "polygon",
      disabled: polygonDisabledInCatchment
    },
    { label: "H3", value: "H3", disabled: showCatchmentPopup },
    {
      label: "Administrative Area",
      value: "adm_area",
      disabled: showCatchmentPopup
    },
    { label: "Geohash", value: "geohash", disabled: showCatchmentPopup },
    ...(isLinestringEnabled
      ? [{ label: "Line", value: "line", disabled: showCatchmentPopup }]
      : [])
  ];
};

const DatasetFilterSection = ({
  isUseNewDatasetExplorerModal,
  countryOptions,
  selectedCountry,
  dataSource,
  spatialAgg,
  license,
  selectedDataSources,
  dataSourceProviders = [],
  setSelectedCountry,
  setDataSource,
  setSpatialAgg,
  setCurrentPage,
  setLicense,
  setSelectedDataSources,
  showCatchmentPopup,
  sessionStorageData,
  setSessionItem
}: Props) => {
  const { isLinestringEnabled, isBVTDataForCatchmentEnabled } =
    useAppFeatures();
  const { catchmentSettingsTemp } = useSelector(
    (state: RootState) => state.spatial
  );

  const lockedCatchmentSpatialAgg = useMemo<
    SpatialAggregationType | undefined
  >(() => {
    // Multi-dataset catchment lock only applies when BVT flag is ON. In the
    // legacy flow there's only ever one POI dataset so no lock is needed.
    if (!isBVTDataForCatchmentEnabled) return undefined;
    // Lock only applies inside the catchment popup flow. Opening the
    // Dataset Explorer from the main menu must not inherit the catchment
    // dataset's spatial agg type, otherwise stale catchmentSettingsTemp
    // from a previous session would keep options disabled.
    if (!showCatchmentPopup) return undefined;
    const datasetConfig = catchmentSettingsTemp?.datasetConfig;
    const firstId = datasetConfig?.visualizationOrder?.[0];
    if (!firstId) return undefined;
    return datasetConfig?.datasets?.[firstId]?.dataset?.data
      ?.spatial_aggregation_type;
  }, [isBVTDataForCatchmentEnabled, catchmentSettingsTemp, showCatchmentPopup]);
  const dataSourceBadgeOptions = [
    {
      label: isUseNewDatasetExplorerModal
        ? "Bvarta & Partner Data"
        : "Bvarta Data",
      value: "bvt",
      // Legacy: BVT data source disabled in catchment popup. New flow: BVT
      // is the catchment dataset source so the option stays enabled.
      ...(!isBVTDataForCatchmentEnabled &&
        showCatchmentPopup && { disabled: true })
    },
    { label: "My Organization", value: "user" }
  ];

  const shouldShowDataSourcesList =
    isUseNewDatasetExplorerModal &&
    dataSource === "bvt" &&
    dataSourceProviders.length > 0;

  const shouldShowDivider = !shouldShowDataSourcesList;

  return (
    <div className="overflow-y-auto w-[30%] pl-[1px] h-[calc(100vh-350px)]">
      <Dropdown
        label="Country"
        name="Country"
        data={countryOptions}
        value={selectedCountry}
        onSelect={(data) => {
          setCurrentPage(1);
          setSelectedCountry(data ?? COUNTRY_DATA[0]);
        }}
      />
      <Divider className="bg-gainsboro w-[100%] h-[1px] mt-4" />
      <BadgeSelect
        label="Data Source"
        className="mt-3 text-[12px]"
        showDivider={shouldShowDivider}
        data={dataSourceBadgeOptions}
        itemSelected={dataSource}
        onSelect={(data) => {
          setCurrentPage(1);
          setDataSource(data as Source);
        }}
      />
      {shouldShowDataSourcesList && (
        <DataSourceList
          dataSourceProviders={dataSourceProviders}
          selectedDataSources={selectedDataSources}
          license={license}
          setSelectedDataSources={(value) => {
            setCurrentPage(1);
            setSelectedDataSources(value);
          }}
          setLicense={(data) => {
            setCurrentPage(1);
            setLicense(data);
          }}
          sessionStorageData={sessionStorageData}
          setSessionItem={setSessionItem}
        />
      )}
      <BadgeSelect
        label="Spatial Aggregation"
        className="mt-3"
        showDivider
        data={getSpatialAggregationOptions(
          showCatchmentPopup,
          isLinestringEnabled,
          lockedCatchmentSpatialAgg,
          isBVTDataForCatchmentEnabled
        )}
        itemSelected={spatialAgg}
        onSelect={(data) => {
          setCurrentPage(1);
          setSpatialAgg(
            spatialAgg === data && !showCatchmentPopup
              ? "all"
              : (data as SpatialAggregationType)
          );
        }}
      />
    </div>
  );
};

export default DatasetFilterSection;
